import React, { useState, useEffect } from 'react';
import {
  subscribe,
  getESGCulturalFrameworkOverview,
  getCulturalAssessments,
  getCulturalRiskMatrix,
  getCompetencyPrograms,
  getImpactMetrics,
  getGovernanceStructure,
  getComplianceFramework,
  getKPIMonitoring,
  getAssessmentById,
  getProgramById,
  getRisksByCategory,
  calculateCulturalTrends,
  getHighRiskCulturalAreas,
  getCriticalCulturalAssessments,
  getCompetencyProgramsByAudience,
  updateAssessmentCompliance,
  updateProgramProgress,
  addCulturalAssessment,
  addCompetencyProgram,
  updateRiskMitigation,
  generateCulturalFrameworkReport,
  exportCulturalFrameworkData,
  refreshCulturalFrameworkData
} from '../../../services/esg/esgCulturalFrameworkService';
import { add as addAuditLog } from '../../../services/bread/auditLogService';

const ESGCulturalFramework = () => {
  const [culturalFrameworkData, setCulturalFrameworkData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterCriteria, setFilterCriteria] = useState({
    category: '',
    riskLevel: '',
    targetAudience: '',
    complianceThreshold: ''
  });

  useEffect(() => {
    const unsubscribe = subscribe(setCulturalFrameworkData);
    setLoading(false);
    
    // Log access
    addAuditLog({ 
      user: 'feros.khan@krotrust.org', 
      action: 'page_access', 
      target: 'esg_cultural_framework', 
      status: 'success' 
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const handleRefreshData = async () => {
    setLoading(true);
    await refreshCulturalFrameworkData();
    addAuditLog({ 
      user: 'feros.khan@krotrust.org', 
      action: 'data_refresh', 
      target: 'esg_cultural_framework_data', 
      status: 'success' 
    });
    setTimeout(() => setLoading(false), 1000);
  };

  const handleAssessmentSelect = (assessment) => {
    setSelectedAssessment(assessment);
    setActiveTab('assessment-detail');
  };

  const handleProgramSelect = (program) => {
    setSelectedProgram(program);
    setActiveTab('program-detail');
  };

  const handleRiskSelect = (risk) => {
    setSelectedRisk(risk);
    setActiveTab('risk-detail');
  };

  const handleExportReport = (assessmentId = null, programId = null, riskCategory = null) => {
    const reportData = generateCulturalFrameworkReport(assessmentId, programId, riskCategory);
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cultural_framework_report_${assessmentId || programId || riskCategory || 'comprehensive'}_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleBulkExport = () => {
    const exportData = exportCulturalFrameworkData('json', filterCriteria);
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cultural_framework_bulk_export_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading || !culturalFrameworkData) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid rgba(124, 58, 237, 0.3)',
            borderTop: '4px solid #7c3aed',
            borderRadius: '50%',
            margin: '0 auto 20px',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: 'white', fontSize: '18px', fontWeight: '600' }}>
            Loading ESG Cultural Framework System...
          </p>
        </div>
      </div>
    );
  }

  const overview = getESGCulturalFrameworkOverview();
  const assessments = getCulturalAssessments();
  const riskMatrix = getCulturalRiskMatrix();
  const competencyPrograms = getCompetencyPrograms();
  const impactMetrics = getImpactMetrics();
  const governance = getGovernanceStructure();
  const compliance = getComplianceFramework();
  const kpis = getKPIMonitoring();
  const trends = calculateCulturalTrends();
  const highRiskAreas = getHighRiskCulturalAreas();
  const criticalAssessments = getCriticalCulturalAssessments();

  // Filter assessments based on criteria
  const filteredAssessments = assessments.filter(assessment => {
    if (filterCriteria.category && !assessment.category.toLowerCase().includes(filterCriteria.category.toLowerCase())) return false;
    if (filterCriteria.riskLevel && assessment.riskLevel !== filterCriteria.riskLevel) return false;
    if (filterCriteria.complianceThreshold && assessment.complianceScore < parseInt(filterCriteria.complianceThreshold)) return false;
    return true;
  });

  const OverviewTab = () => (
    <div>
      {/* Key Metrics Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '20px',
        marginBottom: '30px'
      }}>
        {/* Cultural Assessments Card */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '24px',
          border: '2px solid rgba(59, 130, 246, 0.3)',
          transition: 'all 0.3s ease'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)'
            }}>
              📋
            </div>
            <div>
              <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', color: '#94a3b8', fontWeight: '600' }}>
                Cultural Assessments
              </div>
            </div>
          </div>
          
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{
              fontSize: '48px',
              fontWeight: '900',
              color: '#60a5fa',
              textShadow: '0 2px 8px rgba(59, 130, 246, 0.6)',
              marginBottom: '8px'
            }}>
              {overview.totalAssessments}
            </div>
            <div style={{ fontSize: '14px', color: '#cbd5e1', fontWeight: '600' }}>Total Assessments</div>
          </div>

          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#22c55e', marginBottom: '4px' }}>
              {overview.averageComplianceScore.toFixed(1)}%
            </div>
            <div style={{ fontSize: '13px', color: '#94a3b8', fontWeight: '600' }}>Avg Compliance</div>
          </div>

          <div style={{
            height: '10px',
            background: 'rgba(34, 197, 94, 0.2)',
            borderRadius: '5px',
            overflow: 'hidden',
            marginBottom: '12px'
          }}>
            <div style={{
              height: '100%',
              background: 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)',
              width: `${overview.averageComplianceScore}%`,
              transition: 'width 0.3s ease'
            }}></div>
          </div>

          <div style={{ fontSize: '14px', color: '#cbd5e1', textAlign: 'center', fontWeight: '600' }}>
            ${(overview.totalBudget / 1000000).toFixed(1)}M Total Budget
          </div>
        </div>

        {/* Cultural Preservation Card */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '24px',
          border: '2px solid rgba(34, 197, 94, 0.3)',
          transition: 'all 0.3s ease'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              boxShadow: '0 4px 12px rgba(34, 197, 94, 0.4)'
            }}>
              🏛️
            </div>
            <div>
              <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', color: '#94a3b8', fontWeight: '600' }}>
                Cultural Preservation
              </div>
            </div>
          </div>
          
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{
              fontSize: '48px',
              fontWeight: '900',
              color: '#22c55e',
              textShadow: '0 2px 8px rgba(34, 197, 94, 0.6)',
              marginBottom: '8px'
            }}>
              {overview.culturalPreservationIndex.toFixed(1)}
            </div>
            <div style={{ fontSize: '14px', color: '#cbd5e1', fontWeight: '600' }}>Preservation Index</div>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '13px', color: '#94a3b8', fontWeight: '600' }}>Sites Protected</span>
              <span style={{ fontSize: '13px', color: '#22c55e', fontWeight: '700' }}>{overview.sitesProtected}</span>
            </div>
            <div style={{
              height: '6px',
              background: 'rgba(34, 197, 94, 0.2)',
              borderRadius: '3px',
              overflow: 'hidden'
            }}>
              <div style={{
                height: '100%',
                background: 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)',
                width: `${(overview.sitesProtected / 200) * 100}%`,
                transition: 'width 0.3s ease'
              }}></div>
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '13px', color: '#94a3b8', fontWeight: '600' }}>Communities</span>
              <span style={{ fontSize: '13px', color: '#60a5fa', fontWeight: '700' }}>{overview.communitiesEngaged}</span>
            </div>
            <div style={{
              height: '6px',
              background: 'rgba(59, 130, 246, 0.2)',
              borderRadius: '3px',
              overflow: 'hidden'
            }}>
              <div style={{
                height: '100%',
                background: 'linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%)',
                width: `${(overview.communitiesEngaged / 100) * 100}%`,
                transition: 'width 0.3s ease'
              }}></div>
            </div>
          </div>
        </div>

        {/* Diversity & Inclusion Card */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '24px',
          border: '2px solid rgba(168, 85, 247, 0.3)',
          transition: 'all 0.3s ease'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #a855f7 0%, #c084fc 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              boxShadow: '0 4px 12px rgba(168, 85, 247, 0.4)'
            }}>
              🌈
            </div>
            <div>
              <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', color: '#94a3b8', fontWeight: '600' }}>
                Diversity & Inclusion
              </div>
            </div>
          </div>
          
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{
              fontSize: '48px',
              fontWeight: '900',
              color: '#a855f7',
              textShadow: '0 2px 8px rgba(168, 85, 247, 0.6)',
              marginBottom: '8px'
            }}>
              {overview.diversityScore.toFixed(1)}
            </div>
            <div style={{ fontSize: '14px', color: '#cbd5e1', fontWeight: '600' }}>Diversity Score</div>
          </div>

          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#c084fc', marginBottom: '4px' }}>
              {overview.activePrograms}
            </div>
            <div style={{ fontSize: '13px', color: '#94a3b8', fontWeight: '600' }}>Active Programs</div>
          </div>

          <div style={{ marginBottom: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '13px', color: '#94a3b8', fontWeight: '600' }}>Satisfaction</span>
              <span style={{ fontSize: '13px', color: '#fbbf24', fontWeight: '700' }}>{overview.stakeholderSatisfaction.toFixed(1)}%</span>
            </div>
          </div>

          <div style={{
            height: '6px',
            background: 'rgba(251, 191, 36, 0.2)',
            borderRadius: '3px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              background: 'linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)',
              width: `${overview.stakeholderSatisfaction}%`,
              transition: 'width 0.3s ease'
            }}></div>
          </div>
        </div>

        {/* Risk Management Card */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '24px',
          border: '2px solid rgba(239, 68, 68, 0.3)',
          transition: 'all 0.3s ease'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)'
            }}>
              ⚠️
            </div>
            <div>
              <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', color: '#94a3b8', fontWeight: '600' }}>
                Risk Management
              </div>
            </div>
          </div>
          
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{
              fontSize: '48px',
              fontWeight: '900',
              color: '#fbbf24',
              textShadow: '0 2px 8px rgba(251, 191, 36, 0.6)',
              marginBottom: '8px'
            }}>
              {overview.culturalRisks}
            </div>
            <div style={{ fontSize: '14px', color: '#cbd5e1', fontWeight: '600' }}>Risk Categories</div>
          </div>

          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#ef4444', marginBottom: '4px' }}>
              {overview.culturalIncidents}
            </div>
            <div style={{ fontSize: '13px', color: '#94a3b8', fontWeight: '600' }}>Recent Incidents</div>
          </div>

          <div style={{ marginBottom: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '13px', color: '#94a3b8', fontWeight: '600' }}>High Risk Areas</span>
              <span style={{ fontSize: '13px', color: '#ef4444', fontWeight: '700' }}>{highRiskAreas.length}</span>
            </div>
          </div>

          <div style={{
            height: '8px',
            background: 'rgba(239, 68, 68, 0.2)',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              background: 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)',
              width: `${(highRiskAreas.length / overview.culturalRisks) * 100}%`,
              transition: 'width 0.3s ease'
            }}></div>
          </div>
        </div>
      </div>

      {/* High Risk Cultural Areas */}
      <div className="col-md-8 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-danger text-white">
            <h5 className="mb-0">
              <i className="fas fa-alert me-2"></i>High Risk Cultural Areas
            </h5>
          </div>
          <div className="card-body">
            <div className="row">
              {highRiskAreas.slice(0, 4).map((risk, index) => (
                <div key={index} className="col-md-6 mb-3">
                  <div className="card h-100 border">
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div>
                          <h6 className="mb-0">{risk.category}</h6>
                          <small className="text-muted">{risk.subcategories.slice(0, 2).join(', ')}</small>
                        </div>
                        <div className="text-center">
                          <div className={`h4 mb-0 ${
                            risk.riskLevel === 'Very High' ? 'text-danger' : 'text-warning'
                          }`}>
                            {risk.riskScore.toFixed(1)}
                          </div>
                          <small className="text-muted">Risk Score</small>
                        </div>
                      </div>
                      <div className="mb-2">
                        <small className="fw-bold">Risk Level:</small>
                        <span className={`badge ms-2 ${
                          risk.riskLevel === 'Very High' ? 'bg-danger' :
                          risk.riskLevel === 'High' ? 'bg-warning' : 'bg-success'
                        }`}>
                          {risk.riskLevel}
                        </span>
                      </div>
                      <div className="mb-2">
                        <small className="fw-bold">Mitigation:</small>
                        <div className="small text-truncate">{risk.mitigationStrategy}</div>
                      </div>
                      <div className="row mt-2">
                        <div className="col-6">
                          <div className="text-center">
                            <div className="small fw-bold">{risk.likelihood}%</div>
                            <small className="text-muted">Likelihood</small>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="text-center">
                            <div className="small fw-bold">{risk.impact}%</div>
                            <small className="text-muted">Impact</small>
                          </div>
                        </div>
                      </div>
                      <button 
                        className="btn btn-sm btn-outline-danger w-100 mt-2"
                        onClick={() => handleRiskSelect(risk)}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* KPI Dashboard */}
      <div className="col-md-4 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-primary text-white">
            <h5 className="mb-0">
              <i className="fas fa-chart-line me-2"></i>KPI Monitoring
            </h5>
          </div>
          <div className="card-body">
            {kpis.map((kpi, index) => (
              <div key={index} className="mb-3 p-2 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="fw-bold small">{kpi.name}</span>
                  <span className={`badge ${
                    kpi.current >= kpi.target * 0.9 ? 'bg-success' : 
                    kpi.current >= kpi.target * 0.8 ? 'bg-warning' : 'bg-danger'
                  }`}>
                    {kpi.current.toFixed(1)}
                  </span>
                </div>
                <div className="progress mb-1" style={{ height: '6px' }}>
                  <div 
                    className={`progress-bar ${
                      kpi.current >= kpi.target * 0.9 ? 'bg-success' : 
                      kpi.current >= kpi.target * 0.8 ? 'bg-warning' : 'bg-danger'
                    }`}
                    style={{ width: `${Math.min(100, (kpi.current / kpi.target) * 100)}%` }}
                  ></div>
                </div>
                <div className="d-flex justify-content-between">
                  <small className="text-muted">Target: {kpi.target}</small>
                  <small className={`${kpi.trend.startsWith('+') ? 'text-success' : 'text-danger'}`}>
                    {kpi.trend}
                  </small>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const AssessmentsTab = () => (
    <div className="row">
      {/* Filter Controls */}
      <div className="col-12 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-filter me-2"></i>Filter Cultural Assessments
            </h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-3">
                <label className="form-label">Category</label>
                <select 
                  className="form-select"
                  value={filterCriteria.category}
                  onChange={(e) => setFilterCriteria({...filterCriteria, category: e.target.value})}
                >
                  <option value="">All Categories</option>
                  <option value="Indigenous Rights">Indigenous Rights</option>
                  <option value="Workplace Culture">Workplace Culture</option>
                  <option value="Community Relations">Community Relations</option>
                  <option value="Cultural Heritage">Cultural Heritage</option>
                  <option value="Communication">Communication</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Risk Level</label>
                <select 
                  className="form-select"
                  value={filterCriteria.riskLevel}
                  onChange={(e) => setFilterCriteria({...filterCriteria, riskLevel: e.target.value})}
                >
                  <option value="">All Risk Levels</option>
                  <option value="Very High">Very High</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Min Compliance Score</label>
                <input 
                  type="number"
                  className="form-control"
                  placeholder="e.g., 80"
                  min="0"
                  max="100"
                  value={filterCriteria.complianceThreshold}
                  onChange={(e) => setFilterCriteria({...filterCriteria, complianceThreshold: e.target.value})}
                />
              </div>
              <div className="col-md-3">
                <div className="d-flex align-items-end">
                  <button 
                    className="btn btn-outline-secondary me-2"
                    onClick={() => setFilterCriteria({ category: '', riskLevel: '', targetAudience: '', complianceThreshold: '' })}
                  >
                    <i className="fas fa-times me-2"></i>Clear
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={handleBulkExport}
                  >
                    <i className="fas fa-download me-2"></i>Export
                  </button>
                </div>
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-12">
                <span className="badge bg-info">
                  {filteredAssessments.length} assessments found
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Assessments Table */}
      <div className="col-12">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-clipboard-check me-2"></i>Cultural Impact Assessments
            </h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Assessment</th>
                    <th>Category</th>
                    <th>Region</th>
                    <th>Risk Level</th>
                    <th>Compliance Score</th>
                    <th>Framework Standards</th>
                    <th>Last Assessment</th>
                    <th>Next Review</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAssessments.map((assessment, index) => (
                    <tr key={index}>
                      <td>
                        <div className="fw-bold">{assessment.name}</div>
                        <small className="text-muted">{assessment.id}</small>
                      </td>
                      <td><span className="badge bg-secondary">{assessment.category}</span></td>
                      <td>{assessment.region}</td>
                      <td>
                        <span className={`badge ${
                          assessment.riskLevel === 'Very High' ? 'bg-danger' :
                          assessment.riskLevel === 'High' ? 'bg-warning' :
                          assessment.riskLevel === 'Medium' ? 'bg-info' : 'bg-success'
                        }`}>
                          {assessment.riskLevel}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <span className={`badge me-2 ${
                            assessment.complianceScore >= 90 ? 'bg-success' : 
                            assessment.complianceScore >= 80 ? 'bg-info' :
                            assessment.complianceScore >= 70 ? 'bg-warning' : 'bg-danger'
                          }`}>
                            {assessment.complianceScore.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap">
                          {assessment.frameworkStandards.slice(0, 2).map((framework, idx) => (
                            <span key={idx} className="badge bg-primary me-1 mb-1 small">
                              {framework}
                            </span>
                          ))}
                          {assessment.frameworkStandards.length > 2 && (
                            <span className="badge bg-light text-dark">+{assessment.frameworkStandards.length - 2}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <small>{new Date(assessment.lastAssessment).toLocaleDateString()}</small>
                      </td>
                      <td>
                        <small>{new Date(assessment.nextReview).toLocaleDateString()}</small>
                      </td>
                      <td>
                        <button 
                          className="btn btn-sm btn-outline-primary me-1"
                          onClick={() => handleAssessmentSelect(assessment)}
                          title="View Details"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        <button 
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => handleExportReport(assessment.id)}
                          title="Export Report"
                        >
                          <i className="fas fa-download"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const ProgramsTab = () => (
    <div className="row">
      {/* Competency Programs Cards */}
      <div className="col-12">
        <div className="row">
          {competencyPrograms.map((program, index) => (
            <div key={index} className="col-md-6 mb-4">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header">
                  <h5 className="mb-0">{program.name}</h5>
                  <small className="text-muted">{program.category} • {program.targetAudience}</small>
                </div>
                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-4">
                      <div className="text-center p-2 border rounded">
                        <div className="h5 mb-0">{program.completionRate.toFixed(1)}%</div>
                        <small className="text-muted">Completion</small>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="text-center p-2 border rounded">
                        <div className={`h5 mb-0 ${
                          program.effectivenessScore >= 90 ? 'text-success' :
                          program.effectivenessScore >= 80 ? 'text-warning' : 'text-danger'
                        }`}>
                          {program.effectivenessScore.toFixed(1)}%
                        </div>
                        <small className="text-muted">Effectiveness</small>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="text-center p-2 border rounded">
                        <div className="h6 mb-0">{program.participants.toLocaleString()}</div>
                        <small className="text-muted">Participants</small>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <strong className="small">Duration:</strong>
                    <span className="badge bg-info ms-2">{program.duration}</span>
                  </div>

                  <div className="mb-3">
                    <strong className="small">Format:</strong>
                    <span className="badge bg-secondary ms-2">{program.format}</span>
                  </div>

                  <div className="mb-3">
                    <strong className="small">Certification:</strong>
                    <div className="small text-primary">{program.certification}</div>
                  </div>

                  <div className="mb-3">
                    <strong className="small">Key Outcomes:</strong>
                    <div className="row mt-2">
                      <div className="col-6">
                        <small>Knowledge: {program.outcomes.knowledgeIncrease.toFixed(1)}%</small>
                      </div>
                      <div className="col-6">
                        <small>Behavior: {program.outcomes.behaviorChange.toFixed(1)}%</small>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      Budget: ${(program.budget / 1000000).toFixed(1)}M
                    </small>
                    <button 
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => handleProgramSelect(program)}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const RiskMatrixTab = () => (
    <div className="row">
      {/* Risk Matrix Summary */}
      <div className="col-md-4 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-danger text-white">
            <h6 className="mb-0">
              <i className="fas fa-exclamation-triangle me-2"></i>Very High Risk
            </h6>
          </div>
          <div className="card-body">
            <div className="text-center">
              <div className="h2 text-danger">{riskMatrix.filter(r => r.riskLevel === 'Very High').length}</div>
              <small className="text-muted">Categories</small>
            </div>
          </div>
        </div>
      </div>

      <div className="col-md-4 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-warning text-dark">
            <h6 className="mb-0">
              <i className="fas fa-exclamation me-2"></i>High Risk
            </h6>
          </div>
          <div className="card-body">
            <div className="text-center">
              <div className="h2 text-warning">{riskMatrix.filter(r => r.riskLevel === 'High').length}</div>
              <small className="text-muted">Categories</small>
            </div>
          </div>
        </div>
      </div>

      <div className="col-md-4 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-info text-white">
            <h6 className="mb-0">
              <i className="fas fa-info me-2"></i>Medium Risk
            </h6>
          </div>
          <div className="card-body">
            <div className="text-center">
              <div className="h2 text-info">{riskMatrix.filter(r => r.riskLevel === 'Medium').length}</div>
              <small className="text-muted">Categories</small>
            </div>
          </div>
        </div>
      </div>

      {/* Risk Matrix Table */}
      <div className="col-12">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-table me-2"></i>Cultural Risk Assessment Matrix
            </h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Category</th>
                    <th>Subcategories</th>
                    <th>Risk Level</th>
                    <th>Risk Score</th>
                    <th>Likelihood</th>
                    <th>Impact</th>
                    <th>Mitigation Strategy</th>
                    <th>Monitoring</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {riskMatrix.map((risk, index) => (
                    <tr key={index}>
                      <td>
                        <div className="fw-bold">{risk.category}</div>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap">
                          {risk.subcategories.slice(0, 2).map((sub, idx) => (
                            <span key={idx} className="badge bg-secondary me-1 mb-1 small">
                              {sub}
                            </span>
                          ))}
                          {risk.subcategories.length > 2 && (
                            <span className="badge bg-light text-dark">+{risk.subcategories.length - 2}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${
                          risk.riskLevel === 'Very High' ? 'bg-danger' :
                          risk.riskLevel === 'High' ? 'bg-warning' :
                          risk.riskLevel === 'Medium' ? 'bg-info' : 'bg-success'
                        }`}>
                          {risk.riskLevel}
                        </span>
                      </td>
                      <td>
                        <span className={`fw-bold ${
                          risk.riskScore >= 70 ? 'text-danger' :
                          risk.riskScore >= 50 ? 'text-warning' : 'text-success'
                        }`}>
                          {risk.riskScore.toFixed(1)}
                        </span>
                      </td>
                      <td>{risk.likelihood}%</td>
                      <td>{risk.impact}%</td>
                      <td>
                        <div className="text-truncate" style={{ maxWidth: '200px' }}>
                          {risk.mitigationStrategy}
                        </div>
                      </td>
                      <td>
                        <span className="badge bg-info">{risk.monitoringFrequency}</span>
                      </td>
                      <td>
                        <button 
                          className="btn btn-sm btn-outline-primary me-1"
                          onClick={() => handleRiskSelect(risk)}
                          title="View Details"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        <button 
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => handleExportReport(null, null, risk.category)}
                          title="Export Report"
                        >
                          <i className="fas fa-download"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const ComplianceTab = () => (
    <div className="row">
      {/* Compliance Framework Cards */}
      <div className="col-md-4 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-primary text-white">
            <h5 className="mb-0">
              <i className="fas fa-globe me-2"></i>UNESCO
            </h5>
            <small>Cultural Diversity & Heritage</small>
          </div>
          <div className="card-body">
            <div className="text-center mb-3">
              <div className="h2 text-primary">{compliance.unesco.score.toFixed(1)}%</div>
              <small className="text-muted">Compliance Score</small>
            </div>
            {compliance.unesco.conventions.map((convention, index) => (
              <div key={index} className="mb-2">
                <div className="d-flex justify-content-between">
                  <small className="fw-bold">{convention.name.split(' ')[0]}</small>
                  <small>{convention.compliance.toFixed(1)}%</small>
                </div>
                <div className="progress mt-1" style={{ height: '4px' }}>
                  <div 
                    className={`progress-bar ${
                      convention.compliance >= 90 ? 'bg-success' : 
                      convention.compliance >= 80 ? 'bg-warning' : 'bg-danger'
                    }`}
                    style={{ width: `${convention.compliance}%` }}
                  ></div>
                </div>
              </div>
            ))}
            <div className="mt-3">
              <h6 className="small fw-bold">Recommendations:</h6>
              {compliance.unesco.recommendations.slice(0, 2).map((rec, idx) => (
                <div key={idx} className="small text-muted mb-1">• {rec}</div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="col-md-4 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-success text-white">
            <h5 className="mb-0">
              <i className="fas fa-users me-2"></i>ILO 169
            </h5>
            <small>Indigenous & Tribal Peoples</small>
          </div>
          <div className="card-body">
            <div className="text-center mb-3">
              <div className="h2 text-success">{compliance.ilo169.score.toFixed(1)}%</div>
              <small className="text-muted">Compliance Score</small>
            </div>
            {compliance.ilo169.requirements.map((req, index) => (
              <div key={index} className="mb-2">
                <div className="d-flex justify-content-between">
                  <small className="fw-bold">{req.requirement.split(' ')[0]}</small>
                  <small>{req.compliance.toFixed(1)}%</small>
                </div>
                <div className="progress mt-1" style={{ height: '4px' }}>
                  <div 
                    className={`progress-bar ${
                      req.compliance >= 90 ? 'bg-success' : 
                      req.compliance >= 80 ? 'bg-warning' : 'bg-danger'
                    }`}
                    style={{ width: `${req.compliance}%` }}
                  ></div>
                </div>
              </div>
            ))}
            <div className="mt-3">
              <small className="text-muted">
                Next Assessment: {new Date(compliance.ilo169.nextAssessment).toLocaleDateString()}
              </small>
            </div>
          </div>
        </div>
      </div>

      <div className="col-md-4 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-info text-white">
            <h5 className="mb-0">
              <i className="fas fa-handshake me-2" style={{ color: '#8b5cf6' }}></i>GRI 413
            </h5>
            <small>Local Communities</small>
          </div>
          <div className="card-body">
            <div className="text-center mb-3">
              <div className="h2 text-info">{compliance.gri413.score.toFixed(1)}%</div>
              <small className="text-muted">Compliance Score</small>
            </div>
            {compliance.gri413.indicators.map((indicator, index) => (
              <div key={index} className="mb-2">
                <div className="d-flex justify-content-between">
                  <small className="fw-bold">{indicator.indicator.split(' ')[0]}</small>
                  <small>{indicator.compliance.toFixed(1)}%</small>
                </div>
                <div className="progress mt-1" style={{ height: '4px' }}>
                  <div 
                    className={`progress-bar ${
                      indicator.compliance >= 90 ? 'bg-success' : 
                      indicator.compliance >= 80 ? 'bg-warning' : 'bg-danger'
                    }`}
                    style={{ width: `${indicator.compliance}%` }}
                  ></div>
                </div>
              </div>
            ))}
            <div className="mt-3">
              <small className="text-muted">
                Next Report: {new Date(compliance.gri413.nextReport).toLocaleDateString()}
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Cultural Governance Structure */}
      <div className="col-12 mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-sitemap me-2"></i>Cultural Governance Structure
            </h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-4 mb-3">
                <div className="card h-100 border">
                  <div className="card-body p-3">
                    <h6 className="card-title">Cultural Advisory Board</h6>
                    
                    <div className="mb-2">
                      <div className="d-flex justify-content-between">
                        <small>Total Members</small>
                        <small>{governance.culturalAdvisoryBoard.members}</small>
                      </div>
                    </div>
                    
                    <div className="mb-2">
                      <div className="d-flex justify-content-between">
                        <small>Indigenous Rep.</small>
                        <small>{governance.culturalAdvisoryBoard.indigenousRepresentation}%</small>
                      </div>
                      <div className="progress mt-1" style={{ height: '4px' }}>
                        <div 
                          className="progress-bar bg-success"
                          style={{ width: `${governance.culturalAdvisoryBoard.indigenousRepresentation}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="mb-2">
                      <div className="d-flex justify-content-between">
                        <small>Gender Balance</small>
                        <small>{governance.culturalAdvisoryBoard.genderBalance}%</small>
                      </div>
                      <div className="progress mt-1" style={{ height: '4px' }}>
                        <div 
                          className="progress-bar bg-info"
                          style={{ width: `${governance.culturalAdvisoryBoard.genderBalance}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <small className="text-muted">
                      Meeting: {governance.culturalAdvisoryBoard.meetingFrequency}
                    </small>
                  </div>
                </div>
              </div>

              <div className="col-md-4 mb-3">
                <div className="card h-100 border">
                  <div className="card-body p-3">
                    <h6 className="card-title">Cultural Liaison Network</h6>
                    
                    <div className="mb-2">
                      <div className="d-flex justify-content-between">
                        <small>Community Liaisons</small>
                        <small>{governance.culturalLiaisonNetwork.communityLiaisons}</small>
                      </div>
                    </div>
                    
                    <div className="mb-2">
                      <div className="d-flex justify-content-between">
                        <small>Cultural Experts</small>
                        <small>{governance.culturalLiaisonNetwork.culturalExperts}</small>
                      </div>
                    </div>
                    
                    <div className="mb-2">
                      <div className="d-flex justify-content-between">
                        <small>Language Specialists</small>
                        <small>{governance.culturalLiaisonNetwork.languageSpecialists}</small>
                      </div>
                    </div>
                    
                    <div className="mb-2">
                      <div className="d-flex justify-content-between">
                        <small>Effectiveness</small>
                        <small>{governance.culturalLiaisonNetwork.effectiveness.toFixed(1)}%</small>
                      </div>
                      <div className="progress mt-1" style={{ height: '4px' }}>
                        <div 
                          className="progress-bar bg-warning"
                          style={{ width: `${governance.culturalLiaisonNetwork.effectiveness}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-4 mb-3">
                <div className="card h-100 border">
                  <div className="card-body p-3">
                    <h6 className="card-title">Grievance Mechanism</h6>
                    
                    <div className="mb-2">
                      <div className="d-flex justify-content-between">
                        <small>Cultural Grievances</small>
                        <small>{governance.grievanceMechanism.culturalGrievances}</small>
                      </div>
                    </div>
                    
                    <div className="mb-2">
                      <div className="d-flex justify-content-between">
                        <small>Avg Resolution Time</small>
                        <small>{governance.grievanceMechanism.averageResolutionTime.toFixed(1)} days</small>
                      </div>
                    </div>
                    
                    <div className="mb-2">
                      <div className="d-flex justify-content-between">
                        <small>Satisfaction</small>
                        <small>{governance.grievanceMechanism.satisfactionWithResolution.toFixed(1)}%</small>
                      </div>
                      <div className="progress mt-1" style={{ height: '4px' }}>
                        <div 
                          className="progress-bar bg-success"
                          style={{ width: `${governance.grievanceMechanism.satisfactionWithResolution}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <small className="text-muted">
                      Mediation Success: {governance.grievanceMechanism.mediationSuccess.toFixed(1)}%
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'
    }}>
      {/* Hero Header */}
      <div style={{
        background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #c084fc 100%)',
        padding: '60px 40px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* SVG Pattern Overlay */}
        <svg style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0.1
        }}>
          <pattern id="cultural-pattern" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
            <path d="M 0 30 L 30 30 M 30 0 L 30 60" stroke="white" strokeWidth="1" fill="none"/>
          </pattern>
          <rect x="0" y="0" width="100%" height="100%" fill="url(#cultural-pattern)"/>
        </svg>

        <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
              <div style={{
                width: '70px',
                height: '70px',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '32px',
                border: '2px solid rgba(255, 255, 255, 0.2)'
              }}>
                🌍
              </div>
              <div>
                <h1 style={{
                  margin: 0,
                  fontSize: '42px',
                  fontWeight: '900',
                  color: 'white',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  letterSpacing: '-0.5px'
                }}>
                  ESG Cultural Framework
                </h1>
                <p style={{
                  margin: '8px 0 0 0',
                  fontSize: '18px',
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontWeight: '500'
                }}>
                  Cultural sensitivity, diversity & inclusion, and heritage protection management
                </p>
              </div>
            </div>
            
            <button 
              onClick={handleRefreshData}
              disabled={loading}
              style={{
                padding: '12px 24px',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                color: 'white',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 16px rgba(0,0,0,0.3)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <i className="fas fa-sync-alt"></i> Refresh Data
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: 'rgba(15, 23, 42, 0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(168, 85, 247, 0.3)',
        padding: '20px 40px'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {[
              { id: 'overview', label: '📊 Overview' },
              { id: 'assessments', label: '📋 Assessments' },
              { id: 'programs', label: '🎓 Programs' },
              { id: 'risks', label: '⚠️ Risk Matrix' },
              { id: 'compliance', label: '🛡️ Compliance' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '12px 24px',
                  background: activeTab === tab.id 
                    ? 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)'
                    : 'transparent',
                  border: activeTab === tab.id 
                    ? '2px solid rgba(168, 85, 247, 0.5)'
                    : '2px solid transparent',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: activeTab === tab.id 
                    ? '0 4px 12px rgba(124, 58, 237, 0.4)'
                    : 'none'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.id) {
                    e.target.style.background = 'rgba(168, 85, 247, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.id) {
                    e.target.style.background = 'transparent';
                  }
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '40px' }}>
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'assessments' && <AssessmentsTab />}
        {activeTab === 'programs' && <ProgramsTab />}
        {activeTab === 'risks' && <RiskMatrixTab />}
        {activeTab === 'compliance' && <ComplianceTab />}
      </div>
    </div>
  );
};

export default ESGCulturalFramework;